import Konva from 'konva'
import { getActivePinia } from 'pinia'
import { watch } from 'vue'
import { useImageStore } from '../stores/imageStore.js'

/** Return the Image Store filename represented by a Designer/firmware source. */
export function imageSourceFilename(source) {
    if (typeof source !== 'string' || source.trim() === '') return ''

    const value = source.trim()
    const withoutScheme = value.startsWith('L:/')
        ? value.slice(3)
        : value.startsWith('file://')
            ? value.slice(7)
            : value
    return withoutScheme.split('/').pop() ?? ''
}

/** Resolve either the internal resource UUID or the firmware filename form. */
export function resolveImageResource(source, store) {
    if (!source || !store) return null
    return store.getResourceByUuid(source)
        ?? store.getResourceByFilename(imageSourceFilename(source))
        ?? null
}

/**
 * Return the track image selected by LVGL's BG/INDIC priority for Bar/Slider.
 * The knob (Slider's img_src20) is intentionally not part of this decision.
 */
export function resolveTrackImageSelection(owner) {
    const mainSource = owner?.styleProp?.('main', 'img_src')
    if (typeof mainSource === 'string' && mainSource.trim() !== '') {
        return { part: 'main', source: mainSource }
    }

    const indicatorSource = owner?.styleProp?.('indicator', 'img_src')
    if (typeof indicatorSource === 'string' && indicatorSource.trim() !== '') {
        return { part: 'indicator', source: indicatorSource }
    }

    return null
}

/**
 * Read native dimensions from one Image Store resource. imageSize is persisted
 * by the Image Store, so the lock can be applied before the shared bitmap has
 * finished decoding; the decoded image remains the runtime authority when it
 * is available.
 */
export function imageResourceNativeSize(resource) {
    const imageWidth = Number(resource?.image?.naturalWidth ?? resource?.image?.width ?? 0)
    const imageHeight = Number(resource?.image?.naturalHeight ?? resource?.image?.height ?? 0)
    if (imageWidth > 0 && imageHeight > 0) {
        return { width: imageWidth, height: imageHeight }
    }

    const match = typeof resource?.imageSize === 'string'
        ? resource.imageSize.match(/^\s*(\d+)\s*x\s*(\d+)\s*$/i)
        : null
    if (!match) return null
    const width = Number(match[1])
    const height = Number(match[2])
    return width > 0 && height > 0 ? { width, height } : null
}

/**
 * Convert the Designer's internal image-resource value to the existing
 * firmware JSONL representation used by HaspImage: L:/<filename>.
 * Already-serialized sources are kept intact for import/export roundtrips.
 */
export function imageSourceToWireValue(source, store = activeImageStore()) {
    if (source === undefined || source === null || source === '') return ''
    const resource = resolveImageResource(source, store)
    if (resource?.filename) return `L:/${resource.filename}`
    return String(source)
}

function activeImageStore() {
    const pinia = getActivePinia()
    return pinia ? useImageStore(pinia) : null
}

/**
 * Return the native-size placement used by LVGL7 pattern images.
 *
 * LVGL's non-repeating pattern image is centered in the part's background
 * area.  The part area is only the placement/clipping reference; it never
 * changes the bitmap dimensions.
 */
export function nativeImageBounds(image, contentBounds) {
    const bounds = contentBounds ?? {}
    const x = Number(bounds.x ?? 0)
    const y = Number(bounds.y ?? 0)
    const width = Math.max(0, Number(bounds.width ?? 0))
    const height = Math.max(0, Number(bounds.height ?? 0))
    const naturalWidth = Math.max(0, Number(image?.naturalWidth ?? image?.width ?? 0))
    const naturalHeight = Math.max(0, Number(image?.naturalHeight ?? image?.height ?? 0))

    return {
        x: x + (width - naturalWidth) / 2,
        y: y + (height - naturalHeight) / 2,
        width: naturalWidth,
        height: naturalHeight,
    }
}

/**
 * Attach an Image Store-backed Konva.Image to a widget part.
 *
 * The caller owns the node's geometry; this helper only resolves the selected
 * resource, shares its decoded HTMLImageElement, and keeps the image updated
 * when the style or Image Store changes. `getBounds` returns coordinates in
 * the widget's local coordinate system and may include `visible: false`.
 */
export function attachImageSourcePreview(owner, { part, getBounds }) {
    const bitmapNode = new Konva.Image({ image: null, listening: false })
    // LVGL 7 pattern images support pattern opacity and recolor opacity. The
    // schema deliberately follows Bar/Slider and only exposes img_src, but
    // keeping these style values here makes the preview faithful when they
    // are supplied by an inherited/theme style. source-atop mixes the solid
    // recolor over the decoded image, matching lv_color_mix_premult().
    const recolorNode = new Konva.Rect({
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        fill: '#000000',
        opacity: 0,
        globalCompositeOperation: 'source-atop',
        listening: false,
    })
    let clipBounds = null
    const imageNode = new Konva.Group({ listening: false, visible: false })
    imageNode.add(bitmapNode)
    imageNode.add(recolorNode)
    const store = activeImageStore()
    let stopResourceWatch
    let stopImageWatch
    let currentResource = null

    const updateBounds = () => {
        const bounds = getBounds?.() ?? {}
        const imageBounds = bounds.imageBounds ?? bounds
        clipBounds = bounds.clipBounds ?? imageBounds
        // Use an explicit local-coordinate clip rectangle for every part, so
        // changing part/orientation cannot leave stale crop state behind.
        imageNode.setAttr('clip', clipBounds ? {
            x: Number(clipBounds.x ?? 0),
            y: Number(clipBounds.y ?? 0),
            width: Math.max(0, Number(clipBounds.width ?? 0)),
            height: Math.max(0, Number(clipBounds.height ?? 0)),
        } : undefined)
        const radius = Math.max(0, Number(bounds.clipRadius ?? 0))
        imageNode.setAttr('clipFunc', clipBounds && radius > 0 ? context => {
            const x = Number(clipBounds.x ?? 0)
            const y = Number(clipBounds.y ?? 0)
            const width = Math.max(0, Number(clipBounds.width ?? 0))
            const height = Math.max(0, Number(clipBounds.height ?? 0))
            const corner = Math.min(radius, width / 2, height / 2)
            context.beginPath()
            context.moveTo(x + corner, y)
            context.lineTo(x + width - corner, y)
            context.arcTo(x + width, y, x + width, y + corner, corner)
            context.lineTo(x + width, y + height - corner)
            context.arcTo(x + width, y + height, x + width - corner, y + height, corner)
            context.lineTo(x + corner, y + height)
            context.arcTo(x, y + height, x, y + height - corner, corner)
            context.lineTo(x, y + corner)
            context.arcTo(x, y, x + corner, y, corner)
            context.closePath()
        } : undefined)
        // The bitmap always starts from a clean transform. In particular, do
        // not let a prior renderer/crop/orientation retain scale or crop
        // values that would move the image out of track coordinates.
        const naturalWidth = Number(currentResource?.image?.naturalWidth ?? currentResource?.image?.width ?? 0)
        const naturalHeight = Number(currentResource?.image?.naturalHeight ?? currentResource?.image?.height ?? 0)
        const nativeBounds = nativeImageBounds(currentResource?.image, imageBounds)
        // Keep crop explicit and reset it to the complete source image. This
        // makes a reused Konva.Image deterministic across orientation changes
        // while preserving the same source-to-track mapping for both parts.
        bitmapNode.crop({ x: 0, y: 0, width: naturalWidth, height: naturalHeight })
        bitmapNode.scaleX(1)
        bitmapNode.scaleY(1)
        bitmapNode.rotation(0)
        bitmapNode.skewX(0)
        bitmapNode.skewY(0)
        bitmapNode.offsetX(0)
        bitmapNode.offsetY(0)
        bitmapNode.x(Number(nativeBounds.x ?? 0))
        bitmapNode.y(Number(nativeBounds.y ?? 0))
        bitmapNode.width(Math.max(0, Number(nativeBounds.width ?? 0)))
        bitmapNode.height(Math.max(0, Number(nativeBounds.height ?? 0)))

        const rawPatternOpa = Number(owner.styleProp?.(part, 'pattern_opa'))
        const rawPatternRecolorOpa = Number(owner.styleProp?.(part, 'pattern_recolor_opa'))
        const patternOpa = Number.isFinite(rawPatternOpa)
            ? Math.max(0, Math.min(255, rawPatternOpa))
            : 255
        const patternRecolorOpa = Number.isFinite(rawPatternRecolorOpa)
            ? Math.max(0, Math.min(255, rawPatternRecolorOpa))
            : 0
        const source = owner.styleProp?.(part, 'img_src')
        const recolor = owner.styleProp?.(part, 'pattern_recolor')
        recolorNode.setAttrs({
            x: Number(nativeBounds.x ?? 0),
            y: Number(nativeBounds.y ?? 0),
            width: Math.max(0, Number(nativeBounds.width ?? 0)),
            height: Math.max(0, Number(nativeBounds.height ?? 0)),
            fill: typeof recolor === 'string' && recolor.trim() !== '' && recolor !== source ? recolor : '#000000',
            opacity: patternRecolorOpa / 255,
        })
        bitmapNode.opacity(patternOpa / 255)
        if (bounds.visible === false) imageNode.visible(false)
        else if (currentResource?.image) imageNode.visible(true)
    }

    const attachResource = (resource) => {
        stopImageWatch?.()
        currentResource = resource
        bitmapNode.image(null)
        imageNode.visible(false)
        if (!resource) return

        resource.ensureDecoded()
        stopImageWatch = watch(
            () => resource.image,
            image => {
                bitmapNode.image(image ?? null)
                updateBounds()
                owner.getLayer?.()?.requestRender?.()
            },
            { immediate: true },
        )
    }

    const refresh = () => {
        updateBounds()
        if (!store) return
        const source = owner.styleProp(part, 'img_src')
        attachResource(resolveImageResource(source, store))
        if (source && !store.loaded) {
            store.ensureLoaded().catch(() => {})
        }
    }

    if (store) {
        stopResourceWatch = watch(
            () => [owner.styleProp(part, 'img_src'), store.loaded, store.resources],
            refresh,
            { immediate: true },
        )
    } else {
        updateBounds()
    }

    const styleHandler = refresh
    owner.onStyle(part, 'img_src', styleHandler)
    const styleHandlers = [{ property: 'img_src', handler: styleHandler }]
    // Bar/Slider's existing source handler remains the common path. Add
    // appearance listeners only when a caller actually supplies the LVGL 7
    // pattern properties; ordinary image-resource owners therefore keep the
    // same listener ordering and lifecycle as before.
    for (const property of ['pattern_opa', 'pattern_recolor', 'pattern_recolor_opa']) {
        const value = owner.styleProp?.(part, property)
        const valid = property === 'pattern_recolor'
            ? typeof value === 'string' && value.trim() !== '' && value !== owner.styleProp?.(part, 'img_src')
            : Number.isFinite(Number(value))
        if (!valid) continue
        const handler = refresh
        owner.onStyle(part, property, handler)
        styleHandlers.push({ property, handler })
    }
    owner.on('transform', updateBounds)
    owner.on('transformend', updateBounds)
    owner.on('destroy', () => {
        for (const { property, handler } of styleHandlers) owner.offStyle(part, property, handler)
        owner.off('transform', updateBounds)
        owner.off('transformend', updateBounds)
        stopResourceWatch?.()
        stopImageWatch?.()
    })

    return { imageNode, bitmapNode, updateBounds }
}

/**
 * Keep a Bar/Slider's geometry equal to the native size of its selected BG or
 * INDIC image. This is deliberately shared here because Slider inherits the
 * Bar track renderer and must use exactly the same source priority and
 * resource lifecycle.
 */
export function attachTrackImageSizeLock(owner) {
    const store = activeImageStore()
    let stopResourceWatch
    let stopImageWatch
    let currentResource = null

    const stopCurrentImageWatch = () => {
        stopImageWatch?.()
        stopImageWatch = undefined
        currentResource = null
    }

    const applyResourceSize = (resource) => {
        const size = imageResourceNativeSize(resource)
        if (size) owner.applyTrackImageSize?.(size)
    }

    const attachResource = (resource) => {
        if (resource === currentResource) {
            applyResourceSize(resource)
            return
        }

        stopCurrentImageWatch()
        currentResource = resource
        if (!resource) return

        resource.ensureDecoded?.()
        stopImageWatch = watch(
            () => [resource.image, resource.imageSize],
            () => applyResourceSize(resource),
            { immediate: true },
        )
    }

    const refresh = () => {
        const selection = resolveTrackImageSelection(owner)
        if (!selection) {
            stopCurrentImageWatch()
            owner.clearTrackImageSizeLock?.()
            return
        }

        if (!store) return
        const resource = resolveImageResource(selection.source, store)
        if (resource) {
            attachResource(resource)
            return
        }

        // During the initial lazy Image Store load the resource is not known
        // yet. Keep an existing lock until that load settles; only a confirmed
        // missing source should unlock the object.
        if (!store.loaded) {
            const loading = store.ensureLoaded?.()
            loading?.catch?.(() => {})
            return
        }

        stopCurrentImageWatch()
        owner.clearTrackImageSizeLock?.()
    }

    if (store) {
        stopResourceWatch = watch(
            () => [
                owner.styleProp?.('main', 'img_src'),
                owner.styleProp?.('indicator', 'img_src'),
                store.loaded,
                store.resources,
            ],
            refresh,
            { immediate: true },
        )
    } else {
        refresh()
    }

    const styleHandlers = ['main', 'indicator'].map(part => {
        const handler = refresh
        owner.onStyle?.(part, 'img_src', handler)
        return { part, handler }
    })

    const cleanup = () => {
        for (const { part, handler } of styleHandlers) owner.offStyle?.(part, 'img_src', handler)
        stopResourceWatch?.()
        stopCurrentImageWatch()
    }
    owner.on?.('destroy', cleanup)

    return { refresh, cleanup }
}
